import { Injectable, inject } from '@angular/core';
import { Score } from '../../../_shared/models/csTypes/score';
import { Game, GameFormat } from '../../../_shared/models/game.model';
import { Match } from '../../../_shared/models/match.model';
import { PlayerCourtPosition, PlayerInfo, Point, Server } from '../../../_shared/models/point.model';
import { Set } from '../../../_shared/models/set.model';
import { BoardHelpService } from '../board-help.service';
import { DisciplineEnum } from '../../../_shared/models/csTypes/enums/discipline';
import { MatchUpStatusEnum } from '../../../_shared/models/csTypes/enums/matchup_status';
import {
  IocSelectorOption,
  IocSelectorService,
} from '../../../_shared/components/ioc-selector/ioc-selector.service';
import { TimedPointsOrGames } from '../../../_shared/models/score-format.model';
import { Preferences } from '@capacitor/preferences';
import { AlertController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { ClocksService } from '../../../clocks/clocks.service';

@Injectable({
  providedIn: 'root',
})
export class ScoreService {
  private iocSelectorService = inject(IocSelectorService);
  private alertController = inject(AlertController);
  private clocksService = inject(ClocksService);
  private servingPlayerObservable$ = new Subject<any>(); 
  private resetClockObservable$ = new Subject<boolean>(); 
  private side1PlayerPosition: PlayerCourtPosition = {};
  private side2PlayerPosition: PlayerCourtPosition = {};

  point = {
    0: '0',
    1: '15',
    2: '30',
    3: '40',
    4: 'A',
  };
  constructor(private bhs: BoardHelpService) {}

  updateScoreForServingPlayer(selectedServer: Server, playerPositions: any) {
    return this.servingPlayerObservable$.next({selectedServer, playerPositions:playerPositions});
  }
  updateServingPlayerSubscription(){
    return this.servingPlayerObservable$.asObservable();
  }

  endBoltMatch(boltEnd: boolean) {
    return this.resetClockObservable$.next(boltEnd);
  }
  endBoltMatchSubscription(){
    return this.resetClockObservable$.asObservable();
  }


  getScoreObject(match: Match): Score | undefined {
    // these changes for IOCINIT-66 Incorrect conversion
    if(match.matchStatus === MatchUpStatusEnum.WALKOVER) { 
        return {
            scoreStringSide1: 'WO',
            scoreStringSide2: 'WO',
            side1PointScore: '0',
            side2PointScore: '0',
            sets: [],
            server: null,
        };
    }
    if(match.matchStatus === MatchUpStatusEnum.TBP) {
        return {
            scoreStringSide1: '0-0',
            scoreStringSide2: '0-0',
            side1PointScore: '0',
            side2PointScore: '0',
            sets: [],
            server: null,
        };
    }

    if (!match?.sets?.length) return undefined;

    const score: Score = {
      scoreStringSide1: this.getCurrentMatchScoreString(match, 1),
      scoreStringSide2: this.getCurrentMatchScoreString(match, 2),
      side1PointScore: String(this.bhs.getPointScore(match, 1)),
      side2PointScore: String(this.bhs.getPointScore(match, 2)),
      sets: this.transformSets(match),
      server: this.bhs.getServer(match),
    };

    return score;
  }

  private transformSets(match: Match) {
    const transformedSets = [];
    const scoreFormat = this.bhs.getScoreFormat(
      match.matchFormat,
      match.discipline,
      match.score
    );
    const lastGame: any = this.bhs.getLastGame(match)
    const lastPoint: any = this.bhs.getLastPoint(match);
    const isNewSet = lastGame.gameNumber === 1 && lastPoint.pointNumber === 1;

    match.sets.forEach((set:Set) => {
      const lastGame =
        set.winningSide ||
        set.isCompleted ||
        scoreFormat.timedPointsOrGames === TimedPointsOrGames.POINT
          ? set.games?.[set.games.length - 1]
          : set.games?.[set.games.length - 2];
      const timeout = this.clocksService.getTimeoutValuesFromSet(set, match, 0, isNewSet);
      transformedSets.push({
        setNumber: set.setNumber,
        side1Score: this.transformGameScore(1, lastGame, match.discipline),
        side2Score: this.transformGameScore(2, lastGame, match.discipline),
        side1TiebreakScore: this.getTiebreakScore(lastGame, 1),
        side2TiebreakScore: this.getTiebreakScore(lastGame, 2),
        winningSide: set?.winningSide,
        returnerCourtSides: set?.returnerCourtSides, 
        timeout: timeout.side1.timeoutPerMatch > 0 ? {
          side1: {
            timeoutPerSet: timeout.side1.timeoutPerSet,
            remainingTimeoutsPerSet: timeout.side1.remainingTimeoutsPerSet,
            timeoutPerMatch: timeout.side1.timeoutPerMatch,
            remainingTimeoutsPerMatch: timeout.side1.remainingTimeoutsPerMatch,
          }, side2: {
            timeoutPerSet: timeout.side2.timeoutPerSet,
            remainingTimeoutsPerSet: timeout.side2.remainingTimeoutsPerSet,
            timeoutPerMatch: timeout.side2.timeoutPerMatch,
            remainingTimeoutsPerMatch: timeout.side2.remainingTimeoutsPerMatch,
          }
        } : {}
      });
    });

    return transformedSets;
  }

  transformGameScore(
    sideNumber: number,
    lastGame: Game,
    discipline: DisciplineEnum
  ) {
    if (!lastGame) return '0';

    if (discipline === DisciplineEnum.PICKLEBALL || 
        discipline === DisciplineEnum.BADMINTON ||
        discipline === DisciplineEnum.TABLE_TENNIS ||
        discipline === DisciplineEnum.SQUASH) {
      const lastPoint = lastGame.points[lastGame.points.length - 1];
      return lastPoint ? lastPoint[`side${sideNumber}Score`] || 0 : 0;
    } else {
      return lastGame[`side${sideNumber}Score`];
    }
  }

  private getTiebreakScore(game, sideNumber) {
    if (game?.gameFormat === 'TIEBREAK') {
      const lastPoint = game.points[game.points.length - 1];
      return sideNumber === 1 ? lastPoint.side1Score : lastPoint.side2Score;
    } else {
      return null;
    }
  }

  getCurrentMatchScoreString(match: Match, sideNumber: number = 1) {
    if (!match) return null;
    let score = '';

    match.sets.forEach((set, i) => {
      const lastGame =
        set.games.length < 2 || set.winningSide || set.isCompleted
          ? set.games[set.games.length - 1]
          : set.games[set.games.length - 2];

      const lastPoint = lastGame.winningSide
        ? lastGame.points[lastGame.points.length - 1]
        : lastGame.points[lastGame.points.length - 2];

      if (match.discipline === DisciplineEnum.PICKLEBALL || 
        match.discipline === DisciplineEnum.BADMINTON || 
        match.discipline === DisciplineEnum.TABLE_TENNIS ||
        match.discipline === DisciplineEnum.SQUASH) {
        if (set.winningSide || set.isCompleted) {
          // For completed sets, use the final game scores
          if (lastPoint && lastPoint.side1Score !== undefined && lastPoint.side2Score !== undefined) {
            score +=
              sideNumber === 1
                ? `${lastPoint.side1Score}-${lastPoint.side2Score} `
                : `${lastPoint.side2Score}-${lastPoint.side1Score} `;
          } else {
            // Fallback to game scores if point scores are not available
            score +=
              sideNumber === 1
                ? `${lastGame.side1Score || 0}-${lastGame.side2Score || 0} `
                : `${lastGame.side2Score || 0}-${lastGame.side1Score || 0} `;
          }
        } else {
          if (match.matchStatus !== MatchUpStatusEnum.COMPLETED) {
            if (sideNumber === 1) {
              score += `${this.bhs.getPointScore(
                match,
                1
              )}-${this.bhs.getPointScore(match, 2)}`;
            } else if (sideNumber === 2) {
              score += `${this.bhs.getPointScore(
                match,
                2
              )}-${this.bhs.getPointScore(match, 1)}`;
            }
          }
          const lastPoint = this.bhs.getLastPoint(match);
          if (lastPoint && lastPoint.server && lastPoint.server.serverNumber) {
            score += ` (${lastPoint.server.serverNumber})`;
          }
        }
      } else {
        if (i === 2 && match.matchFormat.includes('F:TB')) {
          score +=
            sideNumber === 1
              ? `[${lastPoint?.side1Score || '0'}-${
                  lastPoint?.side2Score || '0'
                }]`
              : `[${lastPoint?.side2Score || '0'}-${
                  lastPoint?.side1Score || '0'
                }]`;
        } else {
          score +=
            sideNumber === 1
              ? `${lastGame.side1Score}-${lastGame.side2Score} `
              : `${lastGame.side2Score}-${lastGame.side1Score} `;

          if (match.matchStatus !== MatchUpStatusEnum.COMPLETED && lastGame.gameFormat === 'TIEBREAK' && lastGame.winningSide) {
            score = score.slice(0, -1);
            score += `(${
              lastPoint.winningSide === 1
                ? lastPoint.side2Score
                : lastPoint.side1Score
            }) `;
          }
        }
      }
    });

    if (
      match.matchStatus !== MatchUpStatusEnum.COMPLETED &&
      match.discipline !== DisciplineEnum.PICKLEBALL &&
      match.discipline !== DisciplineEnum.BADMINTON &&
      match.discipline !== DisciplineEnum.TABLE_TENNIS &&
      match.discipline !== DisciplineEnum.SQUASH
    ) {
      if (sideNumber === 1) {
        score += `(${this.bhs.getPointScore(match, 1)}-${this.bhs.getPointScore(
          match,
          2
        )})`;
      } else if (sideNumber === 2) {
        score += `(${this.bhs.getPointScore(match, 2)}-${this.bhs.getPointScore(
          match,
          1
        )})`;
      }
    }

    // Add status suffix for specific match statuses
    switch (match.matchStatus) {
        case MatchUpStatusEnum.RETIRED:
          score += ' RET';
        break;
        case MatchUpStatusEnum.SUSPENDED:
          score += ' SUSPENDED';
        break;
        case MatchUpStatusEnum.ABANDONED:
          score += ' ABD';
        break;
        case MatchUpStatusEnum.INCOMPLETE:
          score += ' INC';
        break;
    }

    return score.trim();
  }

  getCurrentScore(
    match: Match,
    setIndex: number,
    game: Game,
    point: Point
  ): string {
    let score = '';

    for (let i = 0; i < setIndex; i++) {
      const set = match.sets[i];
      if (set.winningSide) {
        score += `${set.games[set.games.length - 1].side1Score}-${
          set.games[set.games.length - 1].side2Score
        } `;
      }
    }

    const gameScore = this.getGameScore(match.sets[setIndex], game, point);
    const pointScore = this.getPointScore(point, game);

    if (gameScore !== '0-0') {
      score += `${gameScore} `;
    }

    if (pointScore !== '0-0') {
      score += `${pointScore}`;
    }

    return score;
  }

  getSetScore(set: Set) {
    return `${set.side1Score}-${set.side2Score}`;
  }

  getGameScore(set: Set, game: Game, point: Point): string {
    if (game.winningSide && game.points.length === point.pointNumber)
      return `${game.side1Score}-${game.side2Score}`;
    if (game.gameNumber === 1) return '0-0';
    else
      return `${set.games[game.gameNumber - 2].side1Score}-${
        set.games[game.gameNumber - 2].side2Score
      }`;
  }

  getPointScore(point: Point, game: Game): string {
    return game.gameFormat === GameFormat.STANDARD
      ? `${this.point[point.side1Score]}-${this.point[point.side2Score]}`
      : `${point.side1Score}-${point.side2Score}`;
  }

  isMatchCompletedAndLastSetTiebreak(match: Match, setIndex): boolean {
    // Before displaying last set tiebreak in squared brackets [] make sure that
    // match is completed and tiebreak is actually scored point by point
    const scoreFormat = this.bhs.getScoreFormat(
      match.matchFormat,
      match.discipline || DisciplineEnum.TENNIS,
      match.score
    );

    return match.score?.sets?.length === scoreFormat.numberOfSets &&
      scoreFormat.lastSetTiebreakTo &&
      match.winningSide &&
      match.score.sets.length === setIndex + 1 &&
      this.isTiebreakScoredPointByPoint(match)
      ? true
      : false;
  }

  isTiebreakScoredPointByPoint(match: Match): boolean {
    // Until everything is migrated to score object the app needs to support both models
    // This is introduced in 1.10.0

    if (match.score?.sets?.length) {
      const lastSet = match.score.sets[match.score.sets.length - 1];
      const scoreFormat = this.bhs.getScoreFormat(
        match.matchFormat,
        match.discipline || DisciplineEnum.TENNIS,
        match.score
      );

      return (
        lastSet.side1TiebreakScore >= scoreFormat.lastSetTiebreakTo ||
        lastSet.side1TiebreakScore >= scoreFormat.lastSetTiebreakTo
      );
    } else {
      const lastSetTiebreak = match.sets[match.sets.length - 1].games[0];
      const scoreFormat = this.bhs.getScoreFormat(
        match.matchFormat,
        match.discipline || DisciplineEnum.TENNIS,
        match.score
      );

      return (
        lastSetTiebreak.points[lastSetTiebreak.points.length - 1]?.side1Score >=
          scoreFormat.lastSetTiebreakTo ||
        lastSetTiebreak.points[lastSetTiebreak.points.length - 1]?.side2Score >=
          scoreFormat.lastSetTiebreakTo
      );
    }
  }

  async addRallyLength(): Promise<string> {
    const title = 'Rally Length';
    const showCancelButton = true;
    const iocSelectorOptions: IocSelectorOption[] = [
      {
        label: 'Short rally (0-4)',
        value: '0-4',
      },
      {
        label: 'Medium rally (5-8)',
        value: '5-8',
      },
      {
        label: 'Long rally (9+)',
        value: '9+',
      },
    ];

    const data: string = await this.iocSelectorService.openPopover(
      title,
      iocSelectorOptions,
      showCancelButton
    );

    return data;
  }

  async getServeLocation(): Promise<string> {
    const title = 'Serve Location';
    const showCancelButton = true;
    const iocSelectorOptions: IocSelectorOption[] = [
      {
        label: 'Wide',
        value: 'W',
      },
      {
        label: 'Middle',
        value: 'M',
      },
      {
        label: 'T',
        value: 'T',
      },
    ];

    const data: string = await this.iocSelectorService.openPopover(
      title,
      iocSelectorOptions,
      showCancelButton
    );

    return data;
  }

  async checkIfLocalScoreIsNewer(match: Match): Promise<boolean | void> {
    const storedMatch: Match = JSON.parse(
      (await Preferences.get({ key: match._id }))?.value
    );
    if (storedMatch && storedMatch.updatedAt > match.updatedAt) {
      const alert = await this.alertController.create({
        header: 'Score mismatch',
        subHeader: `Local score: ${this.getCurrentMatchScoreString(
          storedMatch,
          1
        )}`,
        message:
          'Scored stored on device is newer than the score on the cloud. Do you want to use local score?',
        buttons: [
          {
            text: 'Update',
            handler: () => {
              Object.keys(storedMatch).forEach((key) => {
                match[key] = storedMatch[key];
              });

              alert.dismiss(true);
            },
          },
          {
            text: 'Cancel',
            role: 'cancel',
            handler: async () => {
              alert.dismiss(false);
            },
          },
        ],
      });

      await alert.present();

      const { data } = await alert.onDidDismiss();

      return data;
    }
  }

  public getPlayerPosition(side: number): PlayerCourtPosition | undefined {
      return this[`side${side}PlayerPosition`] ?? undefined;
    }
  
  public assignPlayerPositions(players: any, selectedServer: Server): void {
    [selectedServer.sideNumber || 1]?.forEach((side) => {
      players[side].forEach((player: PlayerInfo) => {
        const isServerOrReturner =
          selectedServer.sideNumber === side
            ? player.playerNumber === selectedServer.playerNumber
            : player.playerNumber === selectedServer.returnerPlayerNumber;

        this[`side${side}PlayerPosition`][isServerOrReturner ? 'deuce' : 'ad'] =
          player;
      });
    });
  }

  public resetPlayerPositions(): void {
    this.side1PlayerPosition = {};
    this.side2PlayerPosition = {};
  }
}
